import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { page, Titled } from '#util/title.ts';
import { isWebAuthnSupported } from '#util/webauthn-support.ts';
import { MagicLinkSent } from '../login/magic-link-sent/MagicLinkSent.tsx';
import type { InvalidMagicLinkType } from '../types.ts';
import { MagicLinkInvalid } from './magic-link-invalid/MagicLinkInvalid.tsx';
import { MagicLinkVerify } from './magic-link-verify/MagicLinkVerify.tsx';

const PARAM_SCHEMA = { token: 'scalar', email: 'scalar' } as const;

export function MagicLinkPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invalidState, setInvalidState] = useState<InvalidMagicLinkType | undefined>();
  const [resent, setResent] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [{ email, token: magicLinkToken }] = useUrlParams(PARAM_SCHEMA);

  if (!magicLinkToken) {
    return <Navigate to="/auth/login" replace={true} />;
  }

  const hasTOTP = user?.type === 'user' && user.hasTOTPAuthenticator;
  const hasWebAuthn = user?.type === 'user' && user.hasWebAuthnAuthenticator && isWebAuthnSupported();

  if (resent) {
    return (
      <Titled title={() => page('Magic link')}>
        <MagicLinkSent
          email={email}
          onTryAgain={() => {
            let url = '/auth/login';
            if (email) {
              url = `${url}?${new URLSearchParams({ email }).toString()}`;
            }
            void navigate(url, { replace: true });
          }}
        />
      </Titled>
    );
  }

  if (invalidState) {
    return (
      <Titled title={() => page('Magic link')}>
        <MagicLinkInvalid
          email={email}
          type={invalidState}
          onResent={() => {
            setResent(true);
          }}
        />
      </Titled>
    );
  }

  if (verified) {
    // verifyMagicLink awaits refetchUser(), so user should always be
    // available here. Guard defensively just in case.
    if (!user) {
      return null;
    }

    // User already has MFA enrolled — send to /auth/mfa to authenticate.
    // LoginMFA shows the right options based on what the user has (TOTP, passkey, or both).
    if (hasTOTP || hasWebAuthn) {
      return <Navigate to="../mfa" replace={true} />;
    }

    // No MFA enrolled yet — send to registration.
    // RegisterMFA will show ChooseMFA (passkey vs TOTP) when the browser supports
    // WebAuthn, or go straight to TOTP enrollment when it doesn't.
    return <Navigate to="../mfa/register" replace={true} />;
  }

  return (
    <Titled title={() => page('Magic link')}>
      <MagicLinkVerify
        magicLinkToken={magicLinkToken}
        email={email}
        onSuccess={() => setVerified(true)}
        onInvalidLink={(type) => {
          setInvalidState(type);
        }}
      />
    </Titled>
  );
}
